import React, { useState, useEffect } from 'react';
import { dataService } from '../services/dataService';
import { useData } from '../contexts/DataContext';
import { enrichPermitWithLinks } from '../utils/linkEnricher';
import useOfflineSync from '../hooks/useOfflineSync';
import OfflineBanner from './common/OfflineBanner';

import { permitScope } from '../services/permitService';
export default function MonteurDashboard({ currentUser, onLogout, onClose, onBack, onHome, onOpenExecution, users = [] }) {
    const { adminData, globalLibrary } = useData();
    const [permits, setPermits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fromCache, setFromCache] = useState(false);

    // Admin Filter State
    const [selectedUserFilter, setSelectedUserFilter] = useState('all');
    const [activeTab, setActiveTab] = useState('execution'); // 'execution' (Uitvoering) or 'history' (Historie)

    const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superuser';
    // Use username (email) as the persistent ID, matching PersonnelStep assignment logic
    const activeUserId = currentUser.username;

    // Offline sync for non-admin users
    const offlineSync = useOfflineSync(isAdmin ? null : activeUserId);

    useEffect(() => {
        const loadPermits = async () => {
            setLoading(true);
            try {
                let data = [];
                if (isAdmin) {
                    // Admin: fetch ALL permits online (no offline cache for admin)
                    data = await dataService.getAllPermits(permitScope(currentUser));
                    data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setFromCache(false);
                } else {
                    // Monteur: use offline-first fetch (online → cache fallback)
                    const result = await offlineSync.getPermits();
                    data = result.permits;
                    setFromCache(result.fromCache);
                }
                setPermits(data);
            } catch (error) {
                console.error("Error loading permits:", error);
            } finally {
                setLoading(false);
            }
        };

        loadPermits();
    }, [currentUser, isAdmin, activeUserId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="px-2 py-1 rounded text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/50">Klaar voor Uitvoering</span>;
            case 'in_progress':
                return <span className="px-2 py-1 rounded text-xs font-bold bg-blue-500/20 text-blue-400 border border-blue-500/50">Bezig</span>;
            case 'completed':
                return <span className="px-2 py-1 rounded text-xs font-bold bg-slate-500/20 text-slate-400 border border-slate-500/50">Afgerond</span>;
            default:
                return <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">{status}</span>;
        }
    };

    // Filter permits based on selection (if admin) AND Active Tab
    const filteredPermits = (isAdmin
        ? (selectedUserFilter === 'all' ? permits : permits.filter(p => p.assignedTo === selectedUserFilter))
        : permits
    ).filter(p => {
        if (activeTab === 'execution') {
            // Active: pending, in_progress, assigned (and maybe approved if that means ready for mechanic?)
            // Usually 'approved' by WV means ready for Mechanic.
            // 'pending' might be unassigned or just created? 
            // Let's include everything NOT closed.
            return ['pending', 'assigned', 'in_progress', 'approved'].includes(p.status);
        } else {
            // History: completed, rejected
            // 'completed' means mechanic is done. 'rejected' means closed/cancelled.
            return ['completed', 'rejected'].includes(p.status);
        }
    });

    // Helper to resolve user name from ID
    const getUserName = (uid) => {
        const u = users.find(user => (user.uid === uid || user.username === uid));
        return u ? u.username : uid;
    };

    const handleDelete = async (permit) => {
        if (!window.confirm(`Weet je zeker dat je werkplan "${permit.asset || 'Onbekend'}" wilt verwijderen?\n\nDit kan NIET ongedaan worden gemaakt.`)) {
            return;
        }

        try {
            await dataService.deletePermit(permit.id, currentUser.uid);
            // Optimistic update
            setPermits(prev => prev.filter(p => p.id !== permit.id));
        } catch (error) {
            console.error("Error deleting permit:", error);
            alert("Fout bij verwijderen: " + error.message);
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 text-slate-100 p-4">
            {/* Offline indicator banner */}
            {!isAdmin && (
                <OfflineBanner
                    isOffline={offlineSync.isOffline}
                    pendingCount={offlineSync.pendingCount}
                    isSyncing={offlineSync.isSyncing}
                    lastSync={offlineSync.lastSync}
                    onRetry={offlineSync.tryFlush}
                />
            )}
            <div className={`max-w-4xl mx-auto ${offlineSync.isOffline ? 'pt-10' : ''}`}>
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 pb-4 border-b border-slate-800 gap-4">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button onClick={onBack} className="btn btn-sm btn-secondary text-white shadow-md">
                                ⬅️ Terug
                            </button>
                        )}
                        <div>
                            <h1 className="text-2xl font-bold text-white">
                                {isAdmin ? 'Overzicht Uitvoering (Admin)' : (activeTab === 'execution' ? 'Mijn Klussen' : 'Mijn Historie')}
                            </h1>
                            <p className="text-slate-400 text-sm">
                                {isAdmin ? 'Beheer en monitor alle toegewezen werkplannen.' : `Welkom, ${currentUser.username}`}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {onHome && (
                            <button
                                onClick={onHome}
                                className="btn btn-sm bg-slate-700 hover:bg-slate-600 text-slate-200 border-slate-600"
                            >
                                🏠 Home
                            </button>
                        )}
                        <button
                            onClick={onLogout}
                            className="btn btn-secondary btn-sm text-sm"
                        >
                            Uitloggen
                        </button>
                    </div>
                </header>

                {/* Tabs + Admin Filter Container */}
                <div className="mb-6 flex flex-col gap-4">
                    {/* Tab Buttons */}
                    <div className="flex bg-slate-800 rounded-lg p-1 border border-slate-700/50 w-full md:w-auto self-start">
                        <button
                            onClick={() => setActiveTab('execution')}
                            className={`btn btn-sm px-6 py-2 transition-all ${activeTab === 'execution' ? 'bg-white text-slate-900 shadow-lg scale-105 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border-none'}`}
                        >
                            📋 Uitvoering (Actief)
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`btn btn-sm px-6 py-2 transition-all ${activeTab === 'history' ? 'bg-white text-slate-900 shadow-lg scale-105 font-bold' : 'text-slate-400 hover:text-white hover:bg-slate-700/50 border-none'}`}
                        >
                            🗄️ Historie (Afgerond)
                        </button>
                    </div>

                    {/* Admin User Filter - Now UNDER the tabs for mobile */}
                    {isAdmin && (
                        <div className="p-3 bg-slate-800 rounded-lg border border-slate-700">
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                <label className="text-sm font-bold text-slate-300 whitespace-nowrap">🔍 Technicus:</label>
                                <select
                                    className="input bg-slate-900 border-slate-600 py-2 w-full sm:w-auto sm:min-w-[200px]"
                                    value={selectedUserFilter}
                                    onChange={(e) => setSelectedUserFilter(e.target.value)}
                                >
                                    <option value="all">-- Alle Monteurs --</option>
                                    {[...new Set(permits.map(p => p.assignedTo))].map(uid => (
                                        <option key={uid} value={uid}>
                                            {getUserName(uid)}
                                        </option>
                                    ))}
                                </select>
                                <span className="text-xs text-slate-500">
                                    Toont {filteredPermits.length} van {permits.length} klussen
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-12 text-slate-500 animate-pulse">
                        Laden...
                    </div>
                ) : filteredPermits.length === 0 ? (
                    <div className="text-center py-12 bg-slate-800/50 rounded-lg border border-slate-700">
                        <div className="text-4xl mb-4">📭</div>
                        <h3 className="text-lg font-medium text-white">{activeTab === 'execution' ? 'Geen openstaande klussen' : 'Geen historie gevonden'}</h3>
                        <p className="text-slate-500">
                            {activeTab === 'execution'
                                ? (isAdmin ? "Er zijn momenteel geen actieve werkplannen." : "Er zijn momenteel geen werkplannen aan jou toegewezen.")
                                : (isAdmin ? "Er zijn geen afgeronde werkplannen in deze weergave." : "Je hebt nog geen werkplannen afgerond.")
                            }
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-4">
                        {filteredPermits.map(permit => (
                            <div
                                key={permit.id}
                                onClick={() => {
                                    // Enrich the permit with hierarchy data before opening execution
                                    const hierarchy = adminData?.locations?.[permit.location]?.hierarchy;
                                    const enrichedPermit = enrichPermitWithLinks(permit, hierarchy, globalLibrary);
                                    onOpenExecution(enrichedPermit);
                                }}
                                className="card bg-slate-800 border-l-4 border-l-blue-500 hover:bg-slate-700 !cursor-pointer [&_*]:!cursor-pointer select-none transition-all p-4 group relative"
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg text-white group-hover:text-blue-300 transition-colors">
                                        {permit.ultimoNr || 'Geen Nummer'}
                                    </h3>
                                    <div className="flex gap-2 items-center">
                                        {isAdmin && (
                                            <div className={`text-xs px-2 py-1 rounded border ${permit.assignedTo ? 'bg-purple-900/50 text-purple-200 border-purple-500/30' : 'bg-red-900/50 text-red-200 border-red-500/30'}`}>
                                                {permit.assignedTo ? `👤 ${getUserName(permit.assignedTo)}` : '⚠️ Niet toegewezen'}
                                            </div>
                                        )}
                                        {getStatusBadge(permit.status)}
                                        {/* Delete Button Logic:
                                            1. Superuser: Can delete everything
                                            2. Creator: Can delete their own
                                            3. Admin: Can delete ONLY if permit location matches their assigned location
                                        */}
                                        {(() => {
                                            const isSuperuser = currentUser.role === 'superuser';
                                            const isCreator = permit.applicantId === currentUser.uid || permit.applicantName === currentUser.username;
                                            // Check if admin has specific location rights
                                            const isLocationAdmin = currentUser.role === 'admin' && (
                                                !currentUser.location || // If no location set, assume global admin? Or restrict? Safer to assume restrict if we want strict control, but for now let's assume they might be global. 
                                                // Actually, user request is specific: "Only for his assigned locations".
                                                // If currentUser.location is array or string.
                                                (currentUser.allowedLocations && currentUser.allowedLocations.includes(permit.location)) ||
                                                (currentUser.location === permit.location)
                                            );

                                            if (isSuperuser || isCreator || isLocationAdmin) {
                                                return (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Prevent card open
                                                            handleDelete(permit);
                                                        }}
                                                        className="p-1 hover:bg-red-500/20 rounded text-slate-400 hover:text-red-400 transition-colors"
                                                        title="Werkplan verwijderen"
                                                    >
                                                        🗑️
                                                    </button>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                </div>
                                <p className="text-slate-300 font-medium mb-1">{permit.description}</p>

                                <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-3">
                                    <span className="flex items-center gap-1">
                                        📍 {permit.location} - {permit.hall}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        📅 {new Date(permit.createdAt).toLocaleDateString()}
                                    </span>
                                    {permit.assignedAt && (
                                        <span className="flex items-center gap-1 text-blue-400">
                                            👤 Toegewezen: {new Date(permit.assignedAt).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            </div>
        </div>
    );
}
